// The one owner of the non-production "emulate the app" switch (BL-2342, BL-2450): the gateway
// resolver asks it whether a desktop browser gets the emulated gateway and its real device store.
// Production builds never read or write the flag (ADR 066 — Offline Mode § 1).
import {
  NATIVE_APP_EMULATION_PARAM,
  NATIVE_APP_EMULATION_STORAGE_KEY,
  isNativeAppEmulationAllowed,
} from '@auctogon/admin-isomorphic/isomorphic/offline/native-app-emulation';

/** Set when this page saw `?emulate-native-app=0` turn a stored flag off. */
let turnedOffThisPage = false;

/**
 * This page's answer, decided once: `mayBeNativeApp()` asks on every render, and the resolver
 * already settles the gateway once per page load, so the switch does too.
 */
let pageAnswer: boolean | null = null;

/** Reads the build's env at the literal `process.env.*` sites Next inlines. */
function emulationAllowedInThisBuild(): boolean {
  return isNativeAppEmulationAllowed({
    auctogonEnv: process.env.NEXT_PUBLIC_AUCTOGON_ENV,
    nodeEnv: process.env.NODE_ENV,
  });
}

/** localStorage, or null when the page has none or blocks it. */
function storage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function readStored(): string | null {
  try {
    return storage()?.getItem(NATIVE_APP_EMULATION_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStored(value: '1' | null): void {
  try {
    const store = storage();
    if (value == null) store?.removeItem(NATIVE_APP_EMULATION_STORAGE_KEY);
    else store?.setItem(NATIVE_APP_EMULATION_STORAGE_KEY, value);
  } catch {
    // Blocked storage: the query still answers for this page; nothing is remembered.
  }
}

/**
 * Whether this page emulates the native app, decided on the page's first call.
 * `?emulate-native-app=1` turns it on and remembers it for this browser, and `=0` turns it off.
 * Otherwise the remembered flag answers. Always false in a production build, during a server
 * render, and in a browser that never asked for it.
 *
 * @returns true only outside production, with the switch on.
 * @sideEffect Writes or removes the remembered flag (localStorage, UI state) when the query says so.
 */
export function isNativeAppEmulationOn(): boolean {
  if (!emulationAllowedInThisBuild()) return false;
  // A server render has no page to decide for; the first client call does.
  if (typeof window === 'undefined') return false;
  pageAnswer ??= readSwitch();
  return pageAnswer;
}

/** Applies the query to the remembered flag and answers for this page load. */
function readSwitch(): boolean {
  const requested = new URLSearchParams(window.location?.search ?? '').get(NATIVE_APP_EMULATION_PARAM);
  if (requested === '1') {
    if (readStored() !== '1') writeStored('1');
    return true;
  }
  if (requested === '0') {
    if (readStored() === '1') turnedOffThisPage = true;
    writeStored(null);
    return false;
  }
  return readStored() === '1';
}

/**
 * Whether this page turned emulation off (`?emulate-native-app=0` over a remembered flag), so the
 * service worker an emulated page registered can be removed (BL-2450).
 *
 * @returns true once this page has seen the turn-off.
 */
export function wasNativeAppEmulationTurnedOff(): boolean {
  return turnedOffThisPage;
}

/** Test seam: start a new page load (forget this page's answer and its turn-off). */
export function __resetNativeAppEmulationForTest(): void {
  turnedOffThisPage = false;
  pageAnswer = null;
}
